import React, { useEffect, useRef, useState } from 'react';

/**
 * Input buscador de productos Contífico desde la caché local.
 * Muestra sugerencias mientras escribe. Al seleccionar guarda { contifico_codigo, contifico_producto_id }.
 * Si no hay productos en catálogo, no renderiza nada.
 *
 * Props:
 *   productos     - array de { id, codigo, nombre } del catálogo local
 *   value         - código actualmente seleccionado (contifico_codigo)
 *   onChange      - fn({ contifico_codigo, contifico_producto_id, nombre })
 *   placeholder   - string opcional
 *   label         - string opcional
 *   helpText      - string opcional
 */
export default function ProductoBuscador({
  productos = [],
  value = '',
  onChange,
  placeholder = 'Buscar producto Contífico…',
  label = 'Producto Contífico',
  helpText = 'Deja vacío para usar el del curso.',
}) {
  const [query, setQuery]     = useState('');
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  // Sincronizar query con value externo (ej: al cargar editar)
  useEffect(() => {
    if (!value) { setQuery(''); return; }
    const found = productos.find(p => p.codigo === value);
    setQuery(found ? `${found.codigo} — ${found.nombre}` : value);
  }, [value, productos]);

  // Cerrar al click fuera
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!productos.length) return null;

  const activos = productos.filter(p => p.activo !== false);
  const sugerencias = query.trim()
    ? activos.filter(p =>
        p.codigo.toLowerCase().includes(query.toLowerCase()) ||
        p.nombre.toLowerCase().includes(query.toLowerCase())
      )
    : activos;

  const seleccionar = (p) => {
    setQuery(`${p.codigo} — ${p.nombre}`);
    setAbierto(false);
    onChange?.({ contifico_codigo: p.codigo, contifico_producto_id: p.id, nombre: p.nombre });
  };

  const limpiar = () => {
    setQuery('');
    setAbierto(false);
    onChange?.({ contifico_codigo: null, contifico_producto_id: null, nombre: null });
  };

  return (
    <div className="col-md-6" ref={ref}>
      <label className="form-label">
        {label}
        <small className="text-muted ms-1">(opcional)</small>
      </label>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={query}
          onChange={e => { setQuery(e.target.value); setAbierto(true); }}
          onFocus={() => setAbierto(true)}
          autoComplete="off"
        />
        {query && (
          <button type="button" className="btn btn-outline-secondary" onClick={limpiar} tabIndex={-1}>
            <i className="bi bi-x" />
          </button>
        )}
      </div>
      {abierto && sugerencias.length > 0 && (
        <div
          className="border rounded shadow-sm bg-white position-absolute"
          style={{ zIndex: 1050, width: '100%', maxHeight: 220, overflowY: 'auto' }}
        >
          {sugerencias.map(p => (
            <button
              key={p.id}
              type="button"
              className="d-block w-100 text-start px-3 py-2 border-0 bg-transparent"
              style={{ fontSize: 13 }}
              onMouseDown={() => seleccionar(p)}
            >
              <code className="me-2 text-primary">{p.codigo}</code>
              <span className="text-dark">{p.nombre}</span>
            </button>
          ))}
        </div>
      )}
      {helpText && <small className="text-muted">{helpText}</small>}
    </div>
  );
}
